import React from "react";
import { Instagram, Heart, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import OptimizedImage from "@/components/OptimizedImage";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Curated grid of Instagram-style posts drawn from brand imagery already on file.
// Captions and engagement numbers are illustrative until the live API is wired.
// All cards link to the official handle: https://www.instagram.com/grupxaluca/
const POSTS = [
  {
    image:
      "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
    caption: {
      en: "First light over the kasbah. The palm grove still asleep, the cooks already at the bread.",
      fr: "Première lumière sur la kasbah. La palmeraie dort encore, les cuisiniers sont déjà au pain.",
      es: "Primera luz sobre la kasbah. El palmeral aún duerme, los cocineros ya están en el pan.",
    },
    likes: 1480,
    comments: 38,
    tag: "Arfoud",
    aspect: "square",
  },
  {
    image:
      "https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: {
      en: "A night in the Erg Chebbi — three thousand stars and the distant drum of the Gnawa.",
      fr: "Une nuit dans l'Erg Chebbi — trois mille étoiles et le tambour lointain des Gnawa.",
      es: "Una noche en el Erg Chebbi — tres mil estrellas y el tambor lejano de los Gnawa.",
    },
    likes: 2310,
    comments: 64,
    tag: "Bivouacs Xaluca",
    aspect: "portrait",
  },
  {
    image:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=1200&q=80",
    caption: {
      en: "Argan, beldi soap, mint tea. The slow grammar of the hammam.",
      fr: "Argan, savon beldi, thé à la menthe. La grammaire lente du hammam.",
      es: "Argán, jabón beldi, té con menta. La gramática lenta del hammam.",
    },
    likes: 1124,
    comments: 27,
    tag: "Spa & Wellness",
    aspect: "square",
  },
  {
    image:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80",
    caption: {
      en: "Twelve hours under the embers — méchoui, the way the south insists on cooking.",
      fr: "Douze heures sous la braise — méchoui, comme le sud insiste à cuisiner.",
      es: "Doce horas bajo las brasas — mechoui, como el sur insiste en cocinar.",
    },
    likes: 1865,
    comments: 52,
    tag: "Gastronomy",
    aspect: "square",
  },
  {
    image:
      "https://static.prod-images.emergentagent.com/jobs/4e06df6f-9a63-48ff-b8e2-384249cc96cb/images/3ef3804f701debb6deb08b44c397ef614c4c4d52fd2e912b5f5cbb24b2b0703e.png",
    caption: {
      en: "Refuge Ifess Fess — accessible by mule, written for those who read at altitude.",
      fr: "Refuge Ifess Fess — accessible à dos de mule, pour ceux qui lisent en altitude.",
      es: "Refugio Ifess Fess — accesible en mula, para quienes leen en altitud.",
    },
    likes: 932,
    comments: 19,
    tag: "M'Goun",
    aspect: "portrait",
  },
  {
    image:
      "https://images.unsplash.com/photo-1517825738774-7de9363ef735?auto=format&fit=crop&w=1200&q=80",
    caption: {
      en: "Dromedary caravan to the camp. The dunes do most of the conversation.",
      fr: "Caravane de dromadaires vers le camp. Les dunes font la majorité de la conversation.",
      es: "Caravana de dromedarios al campamento. Las dunas hacen casi toda la conversación.",
    },
    likes: 2740,
    comments: 81,
    tag: "Erg Chebbi",
    aspect: "square",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/it6ekinq_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-2.jpg",
    caption: {
      en: "The Berber-tiled pool at the kasbah, the date palms throwing their last lines.",
      fr: "La piscine en zellige de la kasbah, les dattiers tirent leurs dernières lignes.",
      es: "La piscina de zellige de la kasbah, las palmeras trazan sus últimas líneas.",
    },
    likes: 1456,
    comments: 33,
    tag: "Pool · Arfoud",
    aspect: "square",
  },
  {
    image:
      "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?auto=format&fit=crop&w=1200&q=80",
    caption: {
      en: "Aït Ben Haddou before the bus arrives. UNESCO, but earlier.",
      fr: "Aït Ben Haddou avant l'arrivée des bus. UNESCO, mais plus tôt.",
      es: "Aït Ben Haddou antes de la llegada de los autobuses. UNESCO, pero más temprano.",
    },
    likes: 1980,
    comments: 47,
    tag: "Aït Ben Haddou",
    aspect: "portrait",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6tai22ue_Kasbah%20Hotel%20Xaluca%20-%20Bungalow%20185%20-%20LeoGerzon-9.jpg",
    caption: {
      en: "Bungalow 185 at golden hour — cedar ceilings, tadelakt walls, and a window that finally exhales.",
      fr: "Bungalow 185 à l'heure dorée — plafonds en cèdre, murs en tadelakt, et une fenêtre qui souffle enfin.",
      es: "Bungalow 185 a la hora dorada — techos de cedro, muros de tadelakt y una ventana que por fin respira.",
    },
    likes: 1245,
    comments: 31,
    tag: "Bungalow · Arfoud",
    aspect: "square",
  },
];

const COPY = {
  overline: { en: "Instagram · @grupxaluca", fr: "Instagram · @grupxaluca", es: "Instagram · @grupxaluca" },
  title_a: { en: "The South,", fr: "Le sud,", es: "El sur," },
  title_b: { en: "as we see it.", fr: "tel que nous le voyons.", es: "tal como lo vemos." },
  intro: {
    en: "A daily journal of the houses, the dunes, the table and the people who keep them. Followed by 84k travellers, photographers and chefs from around the world.",
    fr: "Un journal quotidien des maisons, des dunes, de la table et des gens qui les tiennent. Suivi par 84 k voyageurs, photographes et chefs du monde entier.",
    es: "Un diario cotidiano de las casas, las dunas, la mesa y la gente que las cuida. Seguido por 84 k viajeros, fotógrafos y chefs del mundo.",
  },
  follow: { en: "Follow", fr: "Suivre", es: "Seguir" },
  followers: { en: "84k followers", fr: "84 k abonnés", es: "84 k seguidores" },
  posts_count: { en: "1,200+ posts", fr: "1 200+ publications", es: "1 200+ publicaciones" },
  view_post: { en: "View on Instagram", fr: "Voir sur Instagram", es: "Ver en Instagram" },
};

const ASPECT_CLASSES = {
  square: "aspect-square",
  portrait: "aspect-[4/5]",
};

const InstagramSection = () => {
  const { lang } = useLanguage();
  const profileUrl = "https://www.instagram.com/grupxaluca/";

  return (
    <section
      className="py-24 md:py-32 bg-[#FDFBF7] relative overflow-hidden"
      data-testid="section-instagram"
    >
      {/* Soft accent wash */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 0% 0%, rgba(212,163,115,0.18) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky brand column */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <p className="overline flex items-center gap-2">
              <Instagram className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
              {pick(COPY.overline, lang)}
            </p>
            <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
              {pick(COPY.title_a, lang)}
              <br />
              <em className="not-italic text-[#C16542]">{pick(COPY.title_b, lang)}</em>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#5C5248] leading-relaxed max-w-md">
              {pick(COPY.intro, lang)}
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-4 max-w-xs">
              <div className="border-l-2 border-[#D4A373] pl-3" data-testid="instagram-followers">
                <dt className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/70">
                  {pick({ en: "Community", fr: "Communauté", es: "Comunidad" }, lang)}
                </dt>
                <dd className="font-serif-x text-2xl md:text-3xl text-[#2C2621] mt-1 tabular-nums">
                  {pick(COPY.followers, lang)}
                </dd>
              </div>
              <div className="border-l-2 border-[#D4A373] pl-3" data-testid="instagram-posts">
                <dt className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/70">
                  {pick({ en: "Journal", fr: "Journal", es: "Diario" }, lang)}
                </dt>
                <dd className="font-serif-x text-2xl md:text-3xl text-[#2C2621] mt-1 tabular-nums">
                  {pick(COPY.posts_count, lang)}
                </dd>
              </div>
            </dl>

            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="group mt-10 inline-flex items-center gap-3 bg-[#2C2621] hover:bg-[#C16542] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
              data-testid="instagram-follow-cta"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
              {pick(COPY.follow, lang)} @grupxaluca
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </a>

            <p className="mt-5 text-xs text-[#5C5248]/75 leading-relaxed max-w-sm">
              {pick({
                en: "@grupxaluca · Marrakech, Sahara, Atlas",
                fr: "@grupxaluca · Marrakech, Sahara, Atlas",
                es: "@grupxaluca · Marrakech, Sáhara, Atlas",
              }, lang)}
            </p>
          </div>
        </aside>

        {/* Editorial post grid — asymmetric, square + portrait mix */}
        <div className="lg:col-span-8">
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
            data-testid="instagram-grid"
          >
            {POSTS.map((post, i) => {
              const aspectCls = ASPECT_CLASSES[post.aspect] || ASPECT_CLASSES.square;
              return (
                <a
                  key={`ig-${post.tag}-${post.likes}`}
                  href={profileUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={`group relative overflow-hidden bg-[#1A1513] ${aspectCls}`}
                  data-testid={`instagram-post-${i}`}
                  data-cms-scope={`instagram.post.${i}`}
                  aria-label={pick(post.caption, lang)}
                >
                  <OptimizedImage
                    src={post.image}
                    alt={pick(post.caption, lang)}
                    widths={[320, 480, 640, 800]}
                    sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                    objectFit="cover"
                    data-cms-role="image"
                  />

                  {/* Instagram-style flash overlay on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-90 transition-opacity duration-500 mix-blend-multiply"
                    style={{
                      background:
                        "linear-gradient(135deg, #405DE6 0%, #5851DB 18%, #833AB4 35%, #C13584 55%, #E1306C 70%, #FD1D1D 85%, #F77737 100%)",
                    }}
                    aria-hidden="true"
                  />

                  {/* Caption overlay */}
                  <div className="absolute inset-0 flex flex-col justify-between p-4 md:p-5 text-[#FDFBF7] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-[10px] tracking-[0.3em] uppercase bg-[#1A1513]/50 backdrop-blur-sm px-2.5 py-1 border border-[#FDFBF7]/30">
                        {post.tag}
                      </span>
                      <Instagram className="w-4 h-4 shrink-0" strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm leading-snug line-clamp-3 font-light">
                        {pick(post.caption, lang)}
                      </p>
                      <div className="mt-3 flex items-center gap-4 text-[11px] tracking-wider">
                        <span className="inline-flex items-center gap-1.5">
                          <Heart className="w-3.5 h-3.5" strokeWidth={1.8} />
                          {post.likes.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
                          {post.comments}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Subtle bottom-left tag visible at rest */}
                  <span
                    className="absolute bottom-3 left-3 text-[9px] tracking-[0.3em] uppercase text-[#FDFBF7] bg-[#1A1513]/40 backdrop-blur-sm px-2 py-1 opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                    aria-hidden="true"
                  >
                    {post.tag}
                  </span>
                </a>
              );
            })}
          </div>

          {/* Inline secondary CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-[#2C2621]/10">
            <p className="text-sm text-[#5C5248] max-w-md leading-relaxed">
              {pick({
                en: "A new post almost every day — landscapes, kitchens, hosts, hours.",
                fr: "Une nouvelle publication presque chaque jour — paysages, cuisines, hôtes, heures.",
                es: "Una nueva publicación casi todos los días — paisajes, cocinas, anfitriones, horas.",
              }, lang)}
            </p>
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#2C2621] border-b border-[#2C2621]/30 hover:text-[#C16542] hover:border-[#C16542] pb-1 transition-colors"
              data-testid="instagram-view-all-cta"
            >
              {pick({ en: "See the full journal", fr: "Voir le journal complet", es: "Ver el diario completo" }, lang)}
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstagramSection;
